// LinkedIn prospecting — shows two paths:
//   1) Webhook URL to paste into a LinkedIn Lead Gen Form (auto-pulls signups)
//   2) Sales Navigator CSV export → our CSV importer
import { useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Copy, Check, ExternalLink, Linkedin, Upload, Zap } from "lucide-react";

const WEBHOOK_PATH = "/api/v1/webhooks/linkedin-lead-gen";

export default function LeadsProspectLinkedInPage() {
  const [copied, setCopied] = useState(false);
  const [testBusy, setTestBusy] = useState(false);
  const [testResult, setTestResult] = useState<string | null>(null);
  const qc = useQueryClient();

  // Full webhook URL based on the current host.
  const webhookUrl = `${window.location.origin}${WEBHOOK_PATH}`;

  async function copyUrl() {
    await navigator.clipboard.writeText(webhookUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  async function fireTestLead() {
    setTestBusy(true); setTestResult(null);
    try {
      const res = await fetch(`${window.location.origin}${WEBHOOK_PATH}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify([{
          firstName: "Test",
          lastName: `Lead ${Date.now() % 1000}`,
          companyName: "Test Company Inc.",
          emailAddress: `test+${Date.now()}@linkedin.example`,
          phoneNumber: "+1-555-0100",
          jobTitle: "VP Testing",
        }]),
      });
      const j = await res.json();
      if (j.ok) {
        setTestResult(`✓ Ingested ${j.ingested} test lead. Check the Leads list.`);
        qc.invalidateQueries({ queryKey: ["/leads"] });
      } else {
        setTestResult(`Failed: ${j.error ?? "unknown"}`);
      }
    } catch (err: any) {
      setTestResult(`Failed: ${err?.message ?? "network error"}`);
    } finally { setTestBusy(false); }
  }

  return (
    <>
      <PageHeader
        title="Prospect from LinkedIn"
        description="Pull leads from LinkedIn Lead Gen Forms (live webhook) or Sales Navigator CSV exports."
        actions={<Button asChild variant="outline"><Link to="/leads"><ArrowLeft className="mr-1 h-4 w-4"/>Back to Leads</Link></Button>}
      />

      <div className="grid gap-4 p-6 md:grid-cols-2">
        {/* ------------ Option 1: Webhook ------------ */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Zap className="h-4 w-4 text-primary"/>Live webhook
              <Badge variant="default" className="ml-1">Recommended</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <p className="text-muted-foreground">
              Every time someone fills out a LinkedIn Lead Gen Form, the lead lands in your Leads list automatically.
            </p>
            <ol className="ml-5 list-decimal space-y-2">
              <li>
                In LinkedIn Campaign Manager:{" "}
                <a href="https://www.linkedin.com/campaignmanager/accounts" target="_blank" rel="noreferrer" className="text-primary hover:underline inline-flex items-center gap-1">
                  open Campaign Manager <ExternalLink className="h-3 w-3" />
                </a>
              </li>
              <li>Go to your campaign → <span className="font-medium">Lead Gen Forms</span> → pick a form → <span className="font-medium">Integrations</span></li>
              <li>Choose <span className="font-medium">Webhook</span> and paste this URL:</li>
            </ol>
            <div className="flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-2">
              <code className="flex-1 truncate text-xs">{webhookUrl}</code>
              <Button size="sm" variant="outline" onClick={copyUrl}>
                {copied ? <><Check className="mr-1 h-3 w-3"/>Copied</> : <><Copy className="mr-1 h-3 w-3"/>Copy</>}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Leads come in with <code className="rounded bg-muted px-1 py-0.5">source = "LinkedIn Lead Gen"</code>.
            </p>

            <div className="rounded-md border border-dashed p-3">
              <p className="text-xs font-medium">Test it now</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Fire a sample payload at the webhook to make sure it's wired up correctly.
              </p>
              <Button size="sm" variant="outline" className="mt-2" onClick={fireTestLead} disabled={testBusy}>
                {testBusy ? "Firing…" : "Send test lead"}
              </Button>
              {testResult && <p className="mt-2 text-xs">{testResult}</p>}
            </div>

            <p className="rounded-md bg-amber-500/10 p-2 text-xs text-amber-900 dark:text-amber-300">
              <span className="font-semibold">Production:</span> once deployed, replace <code>localhost</code> with your
              public domain (<code>crm.cbcinc.ai</code>) in the URL you give LinkedIn.
            </p>
          </CardContent>
        </Card>

        {/* ------------ Option 2: Sales Navigator CSV ------------ */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Linkedin className="h-4 w-4 text-[#0077b5]"/>Sales Navigator CSV
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <p className="text-muted-foreground">
              Have a saved search, lead list, or account list in Sales Navigator?
              Export it and import here as leads.
            </p>
            <ol className="ml-5 list-decimal space-y-2">
              <li>
                In Sales Navigator, go to{" "}
                <span className="font-medium">Lists → your list → ⋯ → Export</span> (or use a tool like
                {" "}<a href="https://phantombuster.com/automations" target="_blank" rel="noreferrer" className="text-primary hover:underline">PhantomBuster</a>
                {" "}/ <a href="https://www.evaboot.com/" target="_blank" rel="noreferrer" className="text-primary hover:underline">Evaboot</a> to enrich the CSV with emails).
              </li>
              <li>Download the <span className="font-medium">CSV</span>.</li>
              <li>Upload via the button below — the importer auto-detects LinkedIn columns.</li>
            </ol>
            <Button asChild>
              <Link to="/leads/import"><Upload className="mr-1 h-4 w-4"/>Upload CSV</Link>
            </Button>
            <p className="text-xs text-muted-foreground">
              The CSV importer understands <code>First Name</code>, <code>Last Name</code>, <code>Company</code>,
              <code>Email</code>, <code>LinkedIn URL</code>, <code>Title</code>, and more.
            </p>
          </CardContent>
        </Card>

        {/* ------------ Sources summary ------------ */}
        <Card className="md:col-span-2">
          <CardHeader><CardTitle className="text-base">All lead-sourcing options</CardTitle></CardHeader>
          <CardContent className="grid gap-3 md:grid-cols-3">
            <SourceCard
              title="Apollo.io"
              desc="Live B2B database search with 275M+ people."
              href="/leads/prospect/apollo"
            />
            <SourceCard
              title="LinkedIn Lead Gen"
              desc="Webhook-based capture of form submissions."
              current
            />
            <SourceCard
              title="CSV upload"
              desc="Any column-mapped CSV — Sales Navigator, conference list, etc."
              href="/leads/import"
            />
          </CardContent>
        </Card>
      </div>
    </>
  );
}

function SourceCard({ title, desc, href, current }: { title: string; desc: string; href?: string; current?: boolean }) {
  const inner = (
    <Card className={`h-full ${href ? "transition-colors hover:bg-muted/40" : ""}`}>
      <CardContent className="p-4">
        <div className="flex items-center gap-2">
          <span className="font-semibold">{title}</span>
          {current && <Badge variant="secondary" className="text-[10px]">You are here</Badge>}
        </div>
        <p className="mt-1 text-xs text-muted-foreground">{desc}</p>
      </CardContent>
    </Card>
  );
  return href ? <Link to={href}>{inner}</Link> : inner;
}
